import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import {
  calcularCotizacion,
  defaultParametros,
  defaultReglasPricing,
  emptyDocumento,
  initialQuote,
} from '../../utils/cotizadorEngine';
import {
  getCotizadorParametros,
  getCotizadorReglasPricing,
  getCotizadorDeudor,
  guardarCotizacion,
  putCotizadorParametros,
  putCotizadorReglasPricing,
} from '../../api/apiConfig';
import { canAccessCotizadorGerente } from '../../utils/gestionCampanaAccess';
import { TabButton } from './components';
import VistaCliente from './VistaCliente';
import VistaEjecutivo from './VistaEjecutivo';
import VistaGerente from './VistaGerente';

export default function CotizadorFactoring() {
  const { user } = useUser();
  const [vista, setVista] = useState('ejecutivo');
  const [quote, setQuote] = useState({ ...initialQuote });
  const [params, setParams] = useState(defaultParametros());
  const [reglas, setReglas] = useState(defaultReglasPricing());
  const [deudorLoadingIdx, setDeudorLoadingIdx] = useState(-1);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState('');

  const puedeGerente = canAccessCotizadorGerente(user);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, r] = await Promise.all([
          getCotizadorParametros().catch(() => null),
          getCotizadorReglasPricing().catch(() => null),
        ]);
        if (p && p.costo_fondo_mensual != null) setParams(p);
        if (Array.isArray(r?.reglas) && r.reglas.length) {
          setReglas(r.reglas.filter(Boolean));
        }
      } catch {
        /* motor local con defaults */
      }
    };
    load();
  }, []);

  const calc = useMemo(() => calcularCotizacion(quote, params, reglas), [quote, params, reglas]);

  const updateQuote = useCallback((key, value) => {
    setQuote((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateParam = useCallback((key, value) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  }, []);

  const updateRegla = useCallback((idx, field, value) => {
    setReglas((prev) => prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  }, []);

  const updateDocumento = useCallback((idx, field, value) => {
    setQuote((prev) => {
      const docs = [...(prev.documentos || [])];
      docs[idx] = { ...docs[idx], [field]: value };
      return { ...prev, documentos: docs };
    });
  }, []);

  const addDocumento = useCallback(() => {
    setQuote((prev) => ({
      ...prev,
      documentos: [...(prev.documentos || []), emptyDocumento()],
    }));
  }, []);

  const removeDocumento = useCallback((idx) => {
    setQuote((prev) => {
      const docs = (prev.documentos || []).filter((_, i) => i !== idx);
      return { ...prev, documentos: docs.length ? docs : [emptyDocumento()] };
    });
  }, []);

  const buscarDeudor = async (lineIndex) => {
    const rut = quote.documentos?.[lineIndex]?.rut_deudor?.trim();
    if (!rut) return;
    setDeudorLoadingIdx(lineIndex);
    try {
      const data = await getCotizadorDeudor(rut);
      if (data.encontrado && data.clasificacion) {
        updateDocumento(lineIndex, 'clasificacion_bd', data.clasificacion);
        updateDocumento(lineIndex, 'clasificacion_aplicada', data.clasificacion);
        updateDocumento(lineIndex, 'clasificacion_manual', false);
      }
    } catch {
      /* sin clasificación */
    } finally {
      setDeudorLoadingIdx(-1);
    }
  };

  const onGuardar = async () => {
    setGuardando(true);
    setMensaje('');
    try {
      await guardarCotizacion({
        quote,
        resultados: calc,
        ejecutivo: user?.ejecutivo_sistema_factoring,
        equipo: user?.grupo,
        mail: user?.email || user?.mail,
        tipo_usuario: user?.tipo_usuario,
      });
      setMensaje('Cotización guardada correctamente.');
    } catch (e) {
      setMensaje(`Error: ${e.message}`);
    } finally {
      setGuardando(false);
    }
  };

  const onGuardarParams = async () => {
    setGuardando(true);
    setMensaje('');
    try {
      const data = await putCotizadorParametros({
        parametros: params,
        tipo_usuario: user?.tipo_usuario,
        mail: user?.email || user?.mail,
      });
      if (data?.costo_fondo_mensual != null) setParams(data);
      setMensaje('Parámetros guardados.');
    } catch (e) {
      setMensaje(`Error: ${e.message}`);
    } finally {
      setGuardando(false);
    }
  };

  const onGuardarReglas = async () => {
    setGuardando(true);
    setMensaje('');
    try {
      const data = await putCotizadorReglasPricing({
        reglas,
        tipo_usuario: user?.tipo_usuario,
        mail: user?.email || user?.mail,
      });
      if (Array.isArray(data?.reglas) && data.reglas.length) {
        setReglas(data.reglas.filter(Boolean));
      }
      setMensaje('Reglas de pricing guardadas.');
    } catch (e) {
      setMensaje(`Error: ${e.message}`);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-blue-600">Cotizador Factoring</p>
            <h1 className="text-2xl font-bold text-slate-900">Cotización, pricing y control de rentabilidad</h1>
            <p className="text-sm text-slate-600 mt-1">Operación con N facturas y deudores distintos.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-xl bg-slate-100 p-1">
              <TabButton active={vista === 'cliente'} onClick={() => setVista('cliente')}>Cliente</TabButton>
              <TabButton active={vista === 'ejecutivo'} onClick={() => setVista('ejecutivo')}>Ejecutivo</TabButton>
              {puedeGerente && (
                <TabButton active={vista === 'gerente'} onClick={() => setVista('gerente')}>Gerente</TabButton>
              )}
            </div>
            <Link to="/cotizador-factoring/listado" className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
              Listado
            </Link>
          </div>
        </div>
      </div>

      {mensaje && vista === 'ejecutivo' && (
        <div className={`rounded-xl p-4 text-sm ${mensaje.startsWith('Error') ? 'bg-red-50 text-red-800' : 'bg-emerald-50 text-emerald-800'}`}>
          {mensaje}
        </div>
      )}

      {vista === 'cliente' && <VistaCliente quote={quote} calc={calc} updateQuote={updateQuote} updateDocumento={updateDocumento} />}
      {vista === 'ejecutivo' && (
        <VistaEjecutivo
          quote={quote}
          calc={calc}
          params={params}
          reglas={reglas}
          documentos={quote.documentos || []}
          updateQuote={updateQuote}
          updateDocumento={updateDocumento}
          onBuscarDeudor={buscarDeudor}
          deudorLoadingIdx={deudorLoadingIdx}
          onAddDocumento={addDocumento}
          onRemoveDocumento={removeDocumento}
          onGuardar={onGuardar}
          guardando={guardando}
        />
      )}
      {vista === 'gerente' && puedeGerente && (
        <VistaGerente
          params={params}
          reglas={reglas}
          updateParam={updateParam}
          updateRegla={updateRegla}
          onGuardarParams={onGuardarParams}
          onGuardarReglas={onGuardarReglas}
          guardando={guardando}
          mensaje={mensaje}
        />
      )}
    </div>
  );
}
